import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const errText = (e, fallback) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : fallback);

function ImpactSummary({ preview }) {
  if (!preview) return <p className="text-sm text-zinc-500">Checking dockets and invoices…</p>;
  return (
    <div className="space-y-2 rounded-md border border-zinc-200 bg-zinc-50 p-3 text-sm" data-testid="truck-reassign-preview">
      <p data-testid="truck-reassign-load-count"><b>{preview.load_count}</b> docket{preview.load_count === 1 ? "" : "s"} will move (NZD {Number(preview.loads_total).toFixed(2)}).</p>
      {preview.invoices.length ? <>
        <p className="font-medium text-amber-700">These invoices will be voided{preview.invoices.some((i) => i.in_xero) ? " (in Xero too)" : ""} so they can be re-issued:</p>
        <ul className="space-y-1" data-testid="truck-reassign-invoices">
          {preview.invoices.map((i) => (
            <li key={i.id} data-testid={`truck-reassign-invoice-${i.id}`}>
              {i.invoice_number} · {i.status}{i.in_xero ? ` · Xero ${i.xero_status || "linked"}` : ""} · {i.truck_dockets} from this truck
              {i.other_dockets ? `, ${i.other_dockets} other dockets stay with the old company` : ""}
            </li>
          ))}
        </ul>
      </> : <p className="text-zinc-600">None of these dockets are on an active invoice.</p>}
    </div>
  );
}

function RebillPanel({ result, onDone }) {
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState([]);
  const createDrafts = async () => {
    setBusy(true);
    const out = [];
    for (const g of result.rebill) {
      try {
        const { data } = await api.post("/landfill/invoices", { company: g.company, load_ids: g.load_ids, request_key: crypto.randomUUID() });
        out.push(`${data.invoice_number} — ${g.company}`);
      } catch (e) {
        toast.error(`${g.company}: ${errText(e, "Could not create draft")}`);
      }
    }
    setCreated(out);
    setBusy(false);
    if (out.length) toast.success(`${out.length} draft invoice${out.length === 1 ? "" : "s"} created`);
  };
  return (
    <div className="space-y-3 text-sm" data-testid="truck-reassign-result">
      <p>Moved <b>{result.moved_count}</b> dockets to <b>{result.company}</b>.</p>
      {!!result.voided_invoices.length && <p>Voided: {result.voided_invoices.map((i) => i.invoice_number).join(", ")}</p>}
      {result.rebill.length > 0 && !created.length && <>
        <p className="font-medium">Re-issue as draft invoices (nothing is sent until you review):</p>
        <ul className="list-disc pl-5" data-testid="truck-reassign-rebill-list">
          {result.rebill.map((g) => <li key={g.company}>{g.company} — {g.count} dockets</li>)}
        </ul>
      </>}
      {!!created.length && <ul className="list-disc pl-5" data-testid="truck-reassign-created">{created.map((c) => <li key={c}>{c}</li>)}</ul>}
      <DialogFooter>
        <Button variant="outline" onClick={onDone} data-testid="truck-reassign-done">{created.length || !result.rebill.length ? "Done" : "Later"}</Button>
        {result.rebill.length > 0 && !created.length && <Button onClick={createDrafts} disabled={busy} data-testid="truck-reassign-create-drafts">{busy ? "Creating…" : "Create draft invoices"}</Button>}
      </DialogFooter>
    </div>
  );
}

function CompanyPicker({ value, onChange, options, current }) {
  const [open, setOpen] = useState(false);
  const q = value.trim().toLowerCase();
  const shown = options.filter((o) => o.name.toLowerCase() !== (current || "").trim().toLowerCase() && (!q || o.name.toLowerCase().includes(q))).slice(0, 8);
  return (
    <div className="relative">
      <Input id="truck-company" autoComplete="off" value={value} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }} placeholder="Search companies or type a new one" data-testid="truck-reassign-company-input" />
      {open && shown.length > 0 && (
        <ul className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-md border border-zinc-200 bg-white py-1 text-sm shadow-lg" data-testid="truck-reassign-company-options">
          {shown.map((o) => (
            <li key={o.name}>
              <button type="button" className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-orange-50"
                onMouseDown={(e) => { e.preventDefault(); onChange(o.name); setOpen(false); }} data-testid={`truck-reassign-company-option-${o.name}`}>
                <span>{o.name}</span><span className="text-xs text-zinc-400">{o.client ? "Client" : "Landfill company"}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function TruckReassign({ truck, onClose, onChanged }) {
  const [clients, setClients] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const ref = truck ? { rego: truck.rego, from_company: truck.company } : null;

  useEffect(() => {
    if (!truck) return;
    setName(""); setReason(""); setPreview(null); setResult(null);
    api.get("/clients").then((r) => setClients((r.data || []).filter((c) => !c.crm_archived))).catch(() => setClients([]));
    Promise.all([
      api.get("/landfill/companies").then((r) => (r.data || []).map((c) => c.company)).catch(() => []),
      api.get("/landfill/trucks").then((r) => (r.data || []).map((t) => t.company)).catch(() => []),
    ]).then(([a, b]) => setCarriers([...new Set([...a, ...b].filter(Boolean).map((n) => n.trim()))]));
    api.post("/landfill/trucks/reassign/preview", { rego: truck.rego, from_company: truck.company })
      .then((r) => setPreview(r.data)).catch((e) => toast.error(errText(e, "Could not load truck dockets")));
  }, [truck]);

  const lower = (s) => (s || "").trim().toLowerCase();
  const match = clients.find((c) => lower(c.name) === lower(name));
  const carrierMatch = carriers.find((c) => lower(c) === lower(name));
  const options = [];
  clients.forEach((c) => c.name && !options.some((o) => lower(o.name) === lower(c.name)) && options.push({ name: c.name.trim(), client: true }));
  carriers.forEach((n) => !options.some((o) => lower(o.name) === lower(n)) && options.push({ name: n, client: false }));
  options.sort((a, b) => a.name.localeCompare(b.name));
  const submit = async () => {
    setBusy(true);
    try {
      const body = { ...ref, reason, ...(match ? { client_id: match.id } : { new_company: carrierMatch || name.trim() }) };
      const { data } = await api.post("/landfill/trucks/reassign", body);
      setResult(data);
      onChanged();
    } catch (e) {
      toast.error(errText(e, "Could not move this truck"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!truck} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="truck-reassign-dialog">
        <DialogHeader>
          <DialogTitle>Edit truck {truck?.rego}</DialogTitle>
          <DialogDescription>Currently recorded under <b>{truck?.company}</b>. Changing the company moves all of this truck's dockets.</DialogDescription>
        </DialogHeader>
        {result ? <RebillPanel result={result} onDone={onClose} /> : <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="truck-company">Actual company</Label>
            <CompanyPicker value={name} onChange={setName} options={options} current={truck?.company} />
            {name.trim() && <p className="text-xs text-zinc-500" data-testid="truck-reassign-company-hint">{match ? "Existing client" : carrierMatch ? `Existing landfill company — dockets join "${carrierMatch}"` : `New company "${name.trim()}" will be created`}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="truck-reason">Reason (optional)</Label>
            <Input id="truck-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Tipping on Civil NZ account" data-testid="truck-reassign-reason-input" />
          </div>
          <ImpactSummary preview={preview} />
          <DialogFooter>
            <Button variant="outline" onClick={onClose} data-testid="truck-reassign-cancel">Cancel</Button>
            <Button onClick={submit} disabled={busy || !preview || !name.trim() || name.trim().toLowerCase() === (truck?.company || "").trim().toLowerCase()} data-testid="truck-reassign-confirm">{busy ? "Moving…" : "Move truck & dockets"}</Button>
          </DialogFooter>
        </div>}
      </DialogContent>
    </Dialog>
  );
}
